import React, { useState } from 'react';
import { Group, Loader, Stack, Text } from '@mantine/core';
import { Dropzone, type FileRejection } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { IconPhotoPlus, IconUpload, IconX } from '@tabler/icons-react';
import { useSWRConfig } from 'swr';
import {
    ACCEPTED_ASSET_MIME_TYPES,
    ASSET_MAX_BYTES,
    ASSET_MAX_MB,
    uploadAsset,
    validateAssetFile,
    type Asset,
} from '../../../data/Asset';
import { useCsrf } from '../../../hooks/useCsrf';

type AssetDropzoneProps = {
    /** Called once per successfully uploaded file, in the order they finished. */
    onUploaded?: (asset: Asset) => void;
    /** Compact styling for tight spaces, e.g. inside the component form. */
    compact?: boolean;
};

/**
 * Click-to-browse and drag-and-drop upload for PNGs.
 *
 * Uploads run one at a time rather than in parallel: a customer dragging in a
 * batch of large images would otherwise open several concurrent multipart
 * requests, and a single failure in the middle of that is much harder to report
 * usefully than a sequential run.
 */
const AssetDropzone: React.FC<AssetDropzoneProps> = ({ onUploaded, compact = false }) => {
    const { csrfToken } = useCsrf();
    const { mutate } = useSWRConfig();
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

    const handleDrop = async (files: File[]) => {
        if (!files.length) return;

        setBusy(true);
        setProgress({ done: 0, total: files.length });

        const failures: string[] = [];
        let uploaded = 0;

        for (const file of files) {
            try {
                const asset = await uploadAsset(file, csrfToken);
                uploaded += 1;
                onUploaded?.(asset);
            } catch (err) {
                failures.push(err instanceof Error ? err.message : `Could not upload ${file.name}.`);
            } finally {
                setProgress(prev => (prev ? { ...prev, done: prev.done + 1 } : null));
            }
        }

        // One revalidation for the whole batch rather than one per file.
        await mutate('api/assets');

        setBusy(false);
        setProgress(null);

        if (uploaded > 0) {
            notifications.show({
                title: 'Upload complete',
                message: `Added ${uploaded} ${uploaded === 1 ? 'image' : 'images'} to your library.`,
            });
        }

        if (failures.length > 0) {
            notifications.show({
                color: 'red',
                title: failures.length === 1 ? 'Upload failed' : `${failures.length} uploads failed`,
                message: failures.join(' '),
            });
        }
    };

    /**
     * Dropzone rejects on its own accept/maxSize rules, but its reason codes are
     * not phrased for humans. Re-deriving the message from the same validator the
     * upload path uses keeps both routes saying the same thing.
     */
    const handleReject = (rejections: FileRejection[]) => {
        const messages = rejections.map(
            rejection => validateAssetFile(rejection.file) ?? `${rejection.file.name} was rejected.`,
        );

        notifications.show({
            color: 'red',
            title: rejections.length === 1 ? 'File not accepted' : 'Files not accepted',
            message: messages.join(' '),
        });
    };

    return (
        <Dropzone
            onDrop={files => void handleDrop(files)}
            onReject={handleReject}
            accept={ACCEPTED_ASSET_MIME_TYPES}
            maxSize={ASSET_MAX_BYTES}
            loading={busy}
            radius="md"
            p={compact ? 'sm' : 'xl'}
        >
            <Group
                justify="center"
                gap="md"
                mih={compact ? 80 : 140}
                style={{ pointerEvents: 'none' }}
            >
                <Dropzone.Accept>
                    <IconUpload size={compact ? 28 : 44} stroke={1.5} color="var(--mantine-color-violet-6)" />
                </Dropzone.Accept>
                <Dropzone.Reject>
                    <IconX size={compact ? 28 : 44} stroke={1.5} color="var(--mantine-color-red-6)" />
                </Dropzone.Reject>
                <Dropzone.Idle>
                    <IconPhotoPlus size={compact ? 28 : 44} stroke={1.5} color="var(--mantine-color-dimmed)" />
                </Dropzone.Idle>

                <Stack gap={2}>
                    <Text size={compact ? 'sm' : 'lg'} inline>
                        {progress
                            ? `Uploading ${progress.done + 1} of ${progress.total}…`
                            : 'Drag images here or click to browse'}
                    </Text>
                    <Text size="xs" c="dimmed" inline>
                        PNG only, up to {ASSET_MAX_MB} MB each
                    </Text>
                </Stack>

                {busy && <Loader size="sm" />}
            </Group>
        </Dropzone>
    );
};

export default AssetDropzone;
